import React, { useEffect, useState } from 'react';
import { ncService } from '../../services/api';
import authService from '../../services/authService';
import './ListaPrintable.css';

/**
 * #78b (Sessão 32) — PDF da lista de NCs.
 *
 * Rota standalone /exportar-ncs?cliente_id=..&<filtros>. Espelha o padrão do
 * ReportPrintable (PDF individual): busca os dados, renderiza tabela limpa pra
 * impressão e dispara window.print() automaticamente. O usuário escolhe
 * "Salvar como PDF". Reusa o endpoint /export?formato=json (7 colunas + meta).
 */
function getFiltrosFromQuery() {
  const sp = new URLSearchParams(window.location.search);
  const clienteId = sp.get('cliente_id');
  const filtros = {};
  ['status', 'severidade', 'origem_tipo', 'fornecedor_id', 'cliente_emissor', 'data_inicio', 'data_fim', 'eficacia', 'busca']
    .forEach((k) => {
      const v = sp.get(k);
      if (v) filtros[k] = v;
    });
  return { clienteId, filtros };
}

export default function ListaPrintable() {
  const { clienteId, filtros } = getFiltrosFromQuery();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!authService.getToken()) {
        setError('Você precisa estar logado para gerar este documento. Volte ao Qaa.sa e faça login.');
        setLoading(false);
        return;
      }
      if (!clienteId) {
        setError('Cliente ausente na URL.');
        setLoading(false);
        return;
      }
      try {
        const res = await ncService.exportarNCsJson(clienteId, filtros);
        if (cancelled) return;
        setData(res);
        try {
          document.title = `qaasa-ncs-${new Date().toISOString().slice(0, 10)}`;
        } catch (_) { /* ignore */ }
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 403) setError('Apenas administradores e operadores podem exportar.');
        else if (status === 400) setError(err.response?.data?.detail || 'Refine os filtros — conjunto muito grande pra exportar.');
        else setError(`Erro ao gerar o documento: ${err.response?.data?.detail || err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  // Dispara window.print() após carregar (igual ReportPrintable).
  useEffect(() => {
    if (loading || !data || error) return;
    const timer = setTimeout(() => {
      try { window.print(); } catch (_) { /* ignore */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [loading, data, error]);

  if (loading) return <div className="lp-loading">Preparando documento…</div>;
  if (error) {
    return (
      <div className="lp-error">
        <p>{error}</p>
        <p><a href="/">Voltar ao Qaa.sa</a></p>
      </div>
    );
  }

  const colunas = data.colunas || [];
  const rows = data.rows || [];

  return (
    <div className="lp-document">
      <header className="lp-header">
        <h1 className="lp-title">Não Conformidades</h1>
        <div className="lp-meta">
          <span>Gerado em: {data.gerado_em}</span>
          <span>Total: {data.total} NC{data.total !== 1 ? 's' : ''}</span>
        </div>
        <div className="lp-filtros"><strong>Filtros:</strong> {data.filtros_aplicados}</div>
      </header>

      {rows.length === 0 ? (
        <p className="lp-vazio">Nenhuma NC encontrada com os filtros aplicados.</p>
      ) : (
        <table className="lp-table">
          <thead>
            <tr>{colunas.map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => <td key={j}>{cell || '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="lp-footer">Qaa.sa — Quality as a Service</footer>
    </div>
  );
}
